import { cva } from 'class-variance-authority';

const sectionStyles = cva(
  // No GPU-layer promotion or paint containment on sections. translateZ(0),
  // backface-visibility:hidden and contain:paint each make the browser rasterize a
  // section into its own buffer; at fractional section heights on Retina/HiDPI that
  // buffer rounds and leaves a ~1px gap that reveals the dark body background
  // (rgb(16,16,23)) as a dark seam at the top/bottom of LIGHT sections (invisible on
  // dark ones, and it disappears the moment the viewport width changes - e.g. opening
  // devtools - because the fractional heights shift). Painting in normal flow lets the
  // -mt-0.5 overlap actually cover the seam.
  'relative -mt-0.5 scroll-mt-16 overflow-hidden',
  {
    variants: {
      nextComponentIsDark: {
        true: '',
        false: '',
      },
      darkMode: {
        true: 'dark',
        false: '',
      },
      paddingTop: {
        none: 'pt-0',
        xs: 'pt-2',
        sm: 'pt-4',
        md: 'pt-2 sm:pt-4 lg:pt-8',
        lg: 'pt-6.5 sm:pt-12 lg:pt-14',
        xl: 'pt-12 sm:pt-18 lg:pt-24',
        '2xl': 'pt-18 sm:pt-24 lg:pt-32',
      },
      paddingBottom: {
        'responsive-none-mobile': 'pb-0 sm:pb-24 lg:pb-32',
        none: 'pb-0',
        xs: 'pb-2',
        sm: 'pb-4',
        md: 'pb-2 sm:pb-4 lg:pb-8',
        lg: 'pb-12 sm:pb-14 lg:pb-16',
        xl: 'pb-12 sm:pb-18 lg:pb-24',
        '2xl': 'pb-18 sm:pb-24 lg:pb-32',
      },
      sidePadding: {
        default: 'px-4 sm:px-16 lg:px-8',
        none: 'px-0',
        sm: 'px-0 md:px-12 lg:px-24 xl:px-32',
        md: 'px-0 md:px-16 lg:px-32 xl:px-40',
        lg: 'px-0 md:px-20 lg:px-40 xl:px-48',
        xl: 'px-0 md:px-24 lg:px-56 xl:px-64',
        '2xl': 'px-0 md:px-32 lg:px-72 xl:px-80',
      },
      dottedBackground: {
        true: '',
        false: '',
      },
      flareBackground: {
        true: '',
        false: '',
      },
      flareBackgroundTop: {
        true: '',
        false: '',
      },
      hasLinearGradientTransition: {
        true: '',
        false: '',
      },
      hasGradientDark: {
        true: '',
        false: '',
      },
      hasGradientTransition: {
        true: '',
        false: '',
      },
      noBackground: {
        true: 'bg-transparent',
        false: '',
      },
      noContain: {
        true: '',
        // [contain:layout], NOT contain-content: `contain: content` implies `contain: paint`,
        // which rasterizes each section into its own buffer and re-introduces the Retina
        // sub-pixel dark seam this file works to avoid. `contain: layout` keeps the layout
        // isolation perf win without any paint containment. Arbitrary value (not the
        // composable `contain-layout` utility) to emit a plain `contain: layout`.
        false: '[contain:layout]',
      },
      noDarkMode: {
        true: 'bg-primary-25',
        false: '',
      },
      hasGradientTransitionTop: {
        true: '',
        false: '',
      },
      hasGradientTransitionTopBanner: {
        true: '',
        false: '',
      },
      tealBackground: {
        true: 'bg-locked-banner-teal',
        false: '',
      },
    },
    compoundVariants: [
      {
        dottedBackground: true,
        flareBackground: false,
        className: 'bg-dotted',
      },
      {
        dottedBackground: false,
        flareBackground: true,
        className: 'bg-gray-900 bg-cover bg-center bg-no-repeat',
      },
      {
        dottedBackground: true,
        flareBackground: true,
        className: 'background-flare-dotted',
      },
      {
        dottedBackground: false,
        flareBackgroundTop: true,
        className: 'bg-gray-900 bg-cover bg-center bg-no-repeat',
      },
      {
        hasLinearGradientTransition: true,
        nextComponentIsDark: true,
        className: 'bg-linear-gradient-dark bg-cover bg-center bg-no-repeat',
      },
      {
        hasGradientDark: true,
        nextComponentIsDark: false,
        className: 'bg-gradient-dark bg-cover bg-center bg-no-repeat',
      },
      {
        nextComponentIsDark: true,
        hasGradientTransition: true,
        className: 'bg-iconCardDeckGradientDark',
      },
      {
        nextComponentIsDark: false,
        hasGradientTransition: true,
        className: 'bg-iconCardDeckGradient',
      },
      {
        nextComponentIsDark: false,
        hasGradientTransitionTop: true,
        className: 'bg-hasGradientTransitionTop',
      },
      {
        nextComponentIsDark: false,
        hasGradientTransitionTop: true,
        className: 'bg-hasGradientTransitionTopBanner',
      },
      {
        darkMode: true,
        hasGradientTransition: false,
        noBackground: false,
        className: 'bg-gray-900',
      },
      {
        darkMode: false,
        noBackground: false,
        className: 'bg-primary-25',
      },
    ],
    defaultVariants: {
      nextComponentIsDark: false,
      hasGradientTransition: false,
      hasGradientDark: false,
      hasLinearGradientTransition: false,
      dottedBackground: false,
      flareBackground: false,
      noBackground: false,
      noContain: false,
      paddingTop: 'lg',
      paddingBottom: 'lg',
    },
  },
);

export default sectionStyles;
